/**
 * CopaModal — Modal do Álbum BB 2026.
 *
 * Campanha encerrada: modal somente leitura, acessível a partir da linha
 * "Ver álbum" em Extrato → Ações de Engajamento (RE-07). Sem Pacote (não há
 * mais envelopes) e sem colar/trocar (`readOnly` fixo no Álbum) — só
 * visualização de Álbum e Coleção.
 *
 * Nota: "Lendárias" foi integrada inline no Álbum (handoff §3.3 Banda 3).
 *
 * Montado como portal em #modal (react-modal), mesmo padrão do SurpriseBox.
 */

import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import { useAuth } from 'context/AuthContext';
import { useCopaAlbumContext } from './CopaAlbumContext';
import { useCopaThemePlayer } from './CopaThemePlayerContext';
import { RepositorySticker } from 'services/copa.service';
import { registerInteractionCampaign } from 'services/actionlog.service';
import { Album } from './components/Album';
import { Collection } from './components/Collection';
import { CopaOnboardingModal } from './CopaOnboardingModal';
import { CopaRegulamento } from './CopaRegulamento';
import { RecognitionDetailModal } from './RecognitionDetailModal';
import { ThemePlayer } from './components/ThemePlayer';
import styles from './CopaModal.module.scss';

Modal.setAppElement('#modal');

// ---------------------------------------------------------------------------
// Abas de navegação interna (Pacote removido — campanha encerrada, RE-07)
// ---------------------------------------------------------------------------
type Tab = 'album' | 'colecao';

const TABS: { id: Tab; label: string; ariaLabel: string }[] = [
  { id: 'album', label: 'Álbum', ariaLabel: 'Ver álbum de figurinhas' },
  { id: 'colecao', label: 'Coleção', ariaLabel: 'Ver coleção de figurinhas' },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface CopaModalProps {
  /** Controlado externamente (Extrato fecha o modal) */
  isOpen?: boolean;
  onClose?: () => void;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------
export function CopaModal({ isOpen: externalIsOpen, onClose: externalOnClose }: CopaModalProps) {
  const { user } = useAuth();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const { pause: pauseTheme } = useCopaThemePlayer();

  const {
    albumState,
    albumSlots,
    challenges,
    isLoading,
    hasLoadedOnce,
    isFirstTime,
    isComplete,
    legendaryCount,
    conqueredCount,
    handleCollate,
    handleSubstitute,
    completeOnboarding,
    loadChallenges,
    error,
  } = useCopaAlbumContext();

  const [activeTab, setActiveTab] = useState<Tab>('album');
  const [selectedSticker, setSelectedSticker] = useState<RepositorySticker | null>(null);
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [showRules, setShowRules] = useState(false);
  // recognitionId da figurinha clicada (RN-30) — abre RecognitionDetailModal
  const [recognitionRecId, setRecognitionRecId] = useState<string | null>(null);

  // Elegibilidade: user.extra.actions !== false
  const isEligible = user?.extra?.actions !== false;

  useEffect(() => {
    if (externalIsOpen !== undefined) return; // modo controlado externamente
    if (isEligible && !isLoading) {
      setInternalIsOpen(true);
    }
  }, [isEligible, isLoading, externalIsOpen]);

  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;

  // Álbum e Coleção usam os mesmos desafios (lendárias) — uma única carga.
  useEffect(() => {
    if (!isOpen) return;
    loadChallenges();
  }, [isOpen, loadChallenges]);

  function handleClose() {
    if (externalOnClose) {
      externalOnClose();
    } else {
      setInternalIsOpen(false);
    }
    pauseTheme(); // fechar o álbum interrompe a trilha da Copa
    setSelectedSticker(null);
    registerInteractionCampaign('me', 'copa_interaction', { type: 'modal_close' });
  }

  // Foco no título ao abrir (acessibilidade)
  function handleAfterOpen() {
    titleRef.current?.focus();
    registerInteractionCampaign('me', 'copa_interaction', { type: 'modal_open' });
  }

  function handleTabChange(tab: Tab) {
    setActiveTab(tab);
    setSelectedSticker(null);
    registerInteractionCampaign('me', 'copa_interaction', { type: `tab_${tab}` });
  }

  // Separa slots lendários dos demais
  const legendarySlots = albumSlots.filter(slot => slot.tipo === 'lendaria');
  const albumGridSlots = albumSlots.filter(slot => slot.tipo !== 'lendaria');

  // A11Y: o appElement global do react-modal aponta para #modal (container vazio),
  // então o react-modal esconderia apenas #modal do leitor de tela e a árvore do
  // Dashboard (#root) continuaria acessível por trás do diálogo. Passamos o
  // appElement por instância (react-modal usa este sobre o global) para esconder
  // #root enquanto o modal está aberto — sem afetar os demais modais do app.
  const appRoot = typeof document !== 'undefined' ? document.getElementById('root') : null;

  // A11Y (tablist WAI-ARIA APG): roving tabindex + navegação por setas.
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  function handleTabKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    const count = TABS.length;
    let next = -1;
    if (e.key === 'ArrowRight') next = (index + 1) % count;
    else if (e.key === 'ArrowLeft') next = (index - 1 + count) % count;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = count - 1;
    else return;
    e.preventDefault();
    handleTabChange(TABS[next].id);
    tabRefs.current[next]?.focus();
  }

  // Não renderiza nada se inelegível (RN-01).
  if (!isEligible) return null;

  return (
    <>
      {/* Onboarding premium — sobreposto ao modal principal enquanto isFirstTime */}
      <CopaOnboardingModal
        isOpen={isOpen && isFirstTime && hasLoadedOnce}
        onConfirm={() => {
          pauseTheme(); // sair do onboarding rebobina a trilha p/ tocar do início no álbum
          completeOnboarding();
        }}
        onViewRules={() => setShowRules(true)}
      />

      {/* Regras da campanha (regulamento) — aberto pelo onboarding */}
      <CopaRegulamento isOpen={showRules} onClose={() => setShowRules(false)} />

      {/* Detalhe do reconhecimento por trás da figurinha (RN-30) */}
      <RecognitionDetailModal
        recognitionId={recognitionRecId}
        onClose={() => setRecognitionRecId(null)}
      />

      <Modal
        isOpen={isOpen && !isFirstTime}
        onAfterOpen={handleAfterOpen}
        onRequestClose={handleClose}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc
        appElement={appRoot ?? undefined}
        overlayClassName="fixed z-[100] top-0 left-0 w-full h-full bg-black bg-opacity-80 flex flex-col items-center justify-center"
        className={styles.modal}
        portalClassName="relative z-[1000]"
        contentLabel="Álbum BB 2026"
      >
        {/* Cabeçalho */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h1
              ref={titleRef}
              tabIndex={-1}
              className={styles.title}
              aria-label="Álbum BB 2026 — Seleção Joga Junto"
            >
              Álbum BB 2026
            </h1>
            {isComplete && (
              <span className={styles.completeBadge} aria-label="Álbum completo!">
                Completo!
              </span>
            )}
          </div>

          {/* Player do tema da Copa (handoff "Player Tema Copa") — centralizado no header */}
          <div className={styles.headerPlayer}>
            <ThemePlayer />
          </div>

          <div className={styles.headerActions}>
            <button
              className={styles.rulesBtn}
              onClick={() => {
                setShowRules(true);
                registerInteractionCampaign('me', 'copa_interaction', { type: 'view_rules' });
              }}
              aria-label="Ver regras da campanha Álbum BB 2026"
              type="button"
            >
              Regras
            </button>

            <button
              className={styles.closeBtn}
              onClick={handleClose}
              aria-label="Fechar álbum BB 2026"
              type="button"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Navegação por abas */}
        <nav className={styles.tabNav} role="tablist" aria-label="Seções do álbum">
          {TABS.map((tab, index) => (
            <button
              key={tab.id}
              ref={el => {
                tabRefs.current[index] = el;
              }}
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`panel-${tab.id}`}
              id={`tab-${tab.id}`}
              // As abas ficam focáveis por Tab (sem roving tabindex): o Tab passa
              // por cada aba individualmente. As setas (onKeyDown) são um atalho
              // adicional, não a única forma de alcançar as abas.
              className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
              onClick={() => handleTabChange(tab.id)}
              onKeyDown={e => handleTabKeyDown(e, index)}
              aria-label={tab.ariaLabel}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Conteúdo das abas */}
        <div className={styles.content}>
          {error && (
            <div className={styles.errorBanner} role="alert">
              {error}
            </div>
          )}

          <div
            role="tabpanel"
            id="panel-album"
            aria-labelledby="tab-album"
            hidden={activeTab !== 'album'}
          >
            {activeTab === 'album' && (
              <Album
                slots={albumGridSlots}
                legendarySlots={legendarySlots}
                challenges={challenges}
                completedCount={albumState.completedCount}
                conqueredCount={conqueredCount}
                legendaryCount={legendaryCount}
                estadioDays={albumState.estadioDays}
                mascoteBehaviors={albumState.mascoteBehaviors}
                repository={albumState.repository}
                selectedSticker={selectedSticker}
                onCollate={handleCollate}
                onSubstitute={handleSubstitute}
                onClearSelection={() => setSelectedSticker(null)}
                onViewRecognition={setRecognitionRecId}
                readOnly
              />
            )}
          </div>

          <div
            role="tabpanel"
            id="panel-colecao"
            aria-labelledby="tab-colecao"
            hidden={activeTab !== 'colecao'}
          >
            {activeTab === 'colecao' && (
              <Collection
                repository={albumState.repository}
                conqueredCount={conqueredCount}
                onViewRecognition={setRecognitionRecId}
              />
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
